import React, { useState, useContext } from 'react';
import { View } from 'react-native';
import LogInModal from './LogInModal';
import SignUpWizard from './components/setupwizard/SignUpWizard';
import DataOps from '../../data/DataOps';
import { usePhoneLogin } from '../../sms/composite/PhoneLoginManager';
import PhoneVerificationModal from '../../sms/components/PhoneVerificationModal';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import { identifyUser, trackEvent } from '../../utility/telemetry';

interface LogInScreenProps {
  visible: boolean;
  setVisible: (v: boolean) => void;
}

const LogInScreen: React.FC<LogInScreenProps> = ({ visible, setVisible }) => {
  const { setUser, setLikedCamps } = useContext(GlobalStateContext);
  const [showAccountTypeSetup, setShowAccountTypeSetup] = useState(false);
  const [newUserData, setNewUserData] = useState<{ phoneNumber: string; firebaseUid: string } | null>(null);

  // Handle verification completion - either existing user login or new user setup
  const handleVerificationComplete = (userData: { phoneNumber: string; firebaseUid: string } | null, existingUser?: any) => {
    if (existingUser) {
      // Existing user - log them in
      setUser(existingUser);
      setLikedCamps(new Set(existingUser.likedCamps || []));
      identifyUser(existingUser.userId, {
        account_type: existingUser.accountType || 'unknown',
        auth_provider: 'phone',
      });
      trackEvent('auth_existing_user_authenticated', {
        auth_provider: 'phone',
        account_type: existingUser.accountType || 'unknown',
      });
      try { DataOps.saveDataToStorage('user', existingUser); } catch {}
      // Update MRU recent accounts list (max 3, unique by userId)
      try {
        const stored = localStorage.getItem('recentAccounts');
        const list = stored ? JSON.parse(stored) : [];
        const filtered = Array.isArray(list) ? list.filter((a: any) => a.userId !== existingUser.userId) : [];
        const next = [
          { userId: existingUser.userId, firstName: existingUser.firstName, lastName: existingUser.lastName, accountType: existingUser.accountType },
          ...filtered
        ].slice(0, 3);
        localStorage.setItem('recentAccounts', JSON.stringify(next));
      } catch {}
      setVisible(false);
      try { window.location.reload(); } catch {}
    } else if (userData) {
      // New user - show account type setup
      trackEvent('auth_new_user_setup_required', {
        auth_provider: 'phone',
      });
      setNewUserData(userData);
      setShowAccountTypeSetup(true);
    }
  };

  // Use PhoneLoginManager hook
  const phoneLogin = usePhoneLogin({
    firebaseApp: DataOps.app,
    visible,
    onVerificationComplete: handleVerificationComplete,
    recaptchaContainerId: 'recaptcha-container-global',
  });

  const handleSetupClose = () => {
    setShowAccountTypeSetup(false);
    setNewUserData(null);
    setVisible(false);
    // The new user will have been created and set by AccountTypeSetup flow; ensure MRU list updates if possible
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (currentUser?.userId) {
        const stored = localStorage.getItem('recentAccounts');
        const list = stored ? JSON.parse(stored) : [];
        const filtered = Array.isArray(list) ? list.filter((a: any) => a.userId !== currentUser.userId) : [];
        const next = [
          { userId: currentUser.userId, firstName: currentUser.firstName, lastName: currentUser.lastName, accountType: currentUser.accountType },
          ...filtered
        ].slice(0, 3);
        localStorage.setItem('recentAccounts', JSON.stringify(next));
      }
    } catch {}
    try { window.location.reload(); } catch {}
  };

  return (
    <>
      {/* reCAPTCHA container */}
      <View style={{ height: 0, width: 0, overflow: 'hidden' }}>
        <View nativeID="recaptcha-container-global" />
      </View>

      <LogInModal
        visible={visible && !phoneLogin.showVerificationModal && !showAccountTypeSetup}
        setVisible={setVisible}
        onContinue={phoneLogin.handleContinue}
        isLoading={phoneLogin.isLoading}
        phoneError={phoneLogin.phoneError}
      />

      <PhoneVerificationModal
        visible={visible && phoneLogin.showVerificationModal}
        onClose={phoneLogin.handleCloseVerification}
        onVerify={phoneLogin.handleVerify}
        onResend={phoneLogin.handleResend}
        isLoading={phoneLogin.isLoading}
        verificationCode={phoneLogin.verificationCode}
        setVerificationCode={phoneLogin.setVerificationCode}
        phoneE164={phoneLogin.pendingPhoneE164}
        canVerify={!!phoneLogin.confirmationResultRef}
      />

      {newUserData && (
        <SignUpWizard
          visible={showAccountTypeSetup}
          onClose={handleSetupClose}
          phoneNumber={newUserData.phoneNumber}
          firebaseUid={newUserData.firebaseUid}
          entrySource="phone"
        />
      )}
    </>
  );
};

export default LogInScreen;